import { chatRequest, loadChatHistory } from "../../api/chatApi";
import { getInitialMessages, getInitialCTAs } from "../chat.constants";
import { indexProductsFromBlocks, resolveProduct } from "../productIndex";
import { buildErrorMessage, cartItemsToMap } from "./session.helpers";
import {
  apiAddToCart,
  apiClearCart,
  apiDecrementCart,
  apiRemoveCartItem,
} from "../../api/cart.api";

export async function callChatApi({
  text,
  historyOverride,
  chatId,
  cart,
  reqSeqRef,
  setLoading,
  setMessages,
  setCtas,
  setScreen,
  setCart,
  productIndexRef,
}) {
  const myReq = ++reqSeqRef.current;
  setLoading(true);

  try {
    const data = await chatRequest({
      chat_id: chatId,
      message: text,
      history: historyOverride,
      cart,
    });

    if (myReq !== reqSeqRef.current) return;

    const nextBlocks = Array.isArray(data.blocks) ? data.blocks : [];

    if (nextBlocks.length) {
      indexProductsFromBlocks(productIndexRef, nextBlocks);
    }

    setMessages((m) => [
      ...m,
      {
        role: "assistant",
        text: data.bot_message || "Ок",
        blocks: nextBlocks,
        animate: true,
      },
    ]);

    setCtas(Array.isArray(data.ctas) ? data.ctas : []);

    if (Array.isArray(data.actions)) {
      for (const a of data.actions) {
        if (a?.type === "go_to_checkout") {
          setScreen("checkout");
          continue;
        }

        if (a?.type === "clear_cart") {
          try {
            const res = await apiClearCart();
            if (!res?.unauthenticated) {
              setCart(cartItemsToMap(res?.items || []));
            }
          } catch (e) {
            console.error("clear_cart server action error:", e);
          }
          continue;
        }

        if (a?.type === "remove_from_cart") {
          try {
            const productId = String(a?.product_id || "");
            if (!productId) continue;

            const res = await apiRemoveCartItem(productId);
            if (!res?.unauthenticated) {
              setCart(cartItemsToMap(res?.items || []));
            }
          } catch (e) {
            console.error("remove_from_cart server action error:", e);
          }
          continue;
        }

        if (a?.type === "decrement_cart") {
          try {
            const productId = String(a?.product_id || "");
            if (!productId) continue;

            const res = await apiDecrementCart(productId, Number(a?.qty || 1));
            if (!res?.unauthenticated) {
              setCart(cartItemsToMap(res?.items || []));
            }
          } catch (e) {
            console.error("decrement_cart server action error:", e);
          }
          continue;
        }

        if (a?.type === "add_to_cart") {
          try {
            const prod = resolveProduct(productIndexRef, a);
            const product = prod || a?.product || {};
            const productId = String(
              a?.product_id ||
                product?.id ||
                product?.product_id ||
                product?.variant_id ||
                ""
            );

            if (!productId) continue;

            const res = await apiAddToCart(
              { ...product, id: productId, product_id: productId },
              Number(a?.qty || 1)
            );

            if (!res?.unauthenticated) {
              setCart(cartItemsToMap(res?.items || []));
            }
          } catch (e) {
            console.error("add_to_cart server action error:", e);
          }
        }
      }
    }
  } catch (e) {
    if (myReq !== reqSeqRef.current) return;
    setMessages((m) => [...m, buildErrorMessage()]);
  } finally {
    if (myReq === reqSeqRef.current) setLoading(false);
  }
}

export async function loadChatSession({
  externalId,
  setLoading,
  setChatId,
  setMessages,
  setCtas,
  setScreen,
  productIndexRef,
}) {
  const id = String(externalId || "").trim();
  if (!id) return;

  setLoading(true);
  try {
    const data = await loadChatHistory(id);

    const restored = (data.messages || []).map((m) => ({
      role: m.role,
      text: m.text,
      meta: m.meta,
      blocks: m.blocks || m.meta?.blocks || [],
      animate: false,
    }));

    setChatId(id);
    setMessages(restored.length ? restored : getInitialMessages());

    const allBlocks = restored.flatMap((m) => m.blocks || []);
    if (allBlocks.length) {
      indexProductsFromBlocks(productIndexRef, allBlocks);
    }

    setCtas(getInitialCTAs());
    setScreen("chat");
  } catch (e) {
    console.error("loadChat error:", e);
  } finally {
    setLoading(false);
  }
}